/**
 * Format a file size in bytes into a human-readable string.
 * @example formatFileSize(1536) → "2 KB"
 * @example formatFileSize(2_500_000) → "2.4 MB"
 */
export function formatFileSize(bytes: number): string {
  const kb = bytes / 1024;
  return kb >= 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${Math.round(kb)} KB`;
}

/**
 * Derive a short type label from a File's MIME type.
 * @example getFileTypeLabel(pdfFile) → "PDF"
 * @example getFileTypeLabel(jpgFile) → "JPEG"
 */
export function getFileTypeLabel(file: File): string {
  if (file.type === 'application/pdf') return 'PDF';
  const sub = file.type.split('/')[1];
  return sub ? sub.toUpperCase() : 'FILE';
}

/** Check whether the file is an image (jpeg, png, gif, webp, …). */
export function isImageFile(file: File): boolean {
  return file.type.startsWith('image/');
}

/** Check whether the file is a PDF. */
export function isPdfFile(file: File): boolean {
  return file.type === 'application/pdf';
}

/** Classify a file as image, pdf, or unknown. */
export function getFileType(file: File): 'image' | 'pdf' | 'unknown' {
  if (isImageFile(file)) return 'image';
  if (isPdfFile(file)) return 'pdf';
  return 'unknown';
}

/** Guess file type from a URL extension (for server-hosted files). */
export function getFileTypeFromUrl(url: string): 'image' | 'pdf' | 'unknown' {
  const lower = url.toLowerCase();
  if (lower.endsWith('.pdf')) return 'pdf';
  if (/\.(jpe?g|png|gif|webp|svg)(\?|$)/i.test(lower)) return 'image';
  return 'image'; // default to image for server URLs without extension
}

/**
 * Open a file in a new browser tab.
 *
 * Handles both shapes the file form layer produces:
 *   - **`data:` URIs** (base64) — newly-dropped files where the user
 *     hasn't saved yet. We can't `window.open` a `data:` URL directly
 *     (most browsers block it), so we open a blank tab and write a
 *     small HTML wrapper with `<embed>` (PDF) or `<img>` (image).
 *   - **Remote URLs** — already-uploaded files served from the CDN.
 *     `window.open(url, '_blank')` is enough; the browser picks the
 *     right viewer (PDF.js / image preview).
 *
 * Mirrors the logic that used to live inline inside `FileThumbnail`
 * so consumers (FileUploader's preview button, custom toolbars) share
 * a single canonical implementation.
 */
export function openFilePreview(src: string): void {
  if (!src) return;

  if (src.startsWith('data:')) {
    const win = window.open('', '_blank');
    if (!win) return;
    const isDataPdf = src.startsWith('data:application/pdf');
    if (isDataPdf) {
      win.document.write(
        `<html><body style="margin:0"><embed src="${src}" width="100%" height="100%" type="application/pdf"></body></html>`,
      );
    } else {
      win.document.write(
        `<html><body style="margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#111"><img src="${src}" style="max-width:100%;max-height:100vh;object-fit:contain"></body></html>`,
      );
    }
    win.document.close();
    return;
  }

  window.open(src, '_blank');
}
